/**
 * One configuration setting: what it governs, what is in force, which partners
 * differ, and every change ever made to it.
 *
 * Three things on this screen are deliberate.
 *
 * Validation is not repeated here. The range, the type and the permitted values
 * are enforced by the schema, and the message shown on a refusal is the
 * database's own. A range check written in this component as well would be a
 * second rule to keep in step, and the first time the two disagreed the screen
 * would accept a value the schema then refused, which reads as a bug in the
 * wrong place.
 *
 * The change history is the audit trail filtered, not a log this screen keeps.
 * A second history would be the one that disagreed the first time somebody
 * changed a value another way.
 *
 * A published setting warns before it is changed, because participants have
 * been told the value and changing it changes what the platform promised.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../../api/client'
import type { Dict, SectionMeta } from '../../api/types'
import { useSession } from '../../app/session'
import { Badge, KV, Note } from '../../components/bits'
import { PageHead } from '../../components/PageHead'
import { Panel, Cols } from '../../components/Panel'

export function SettingRecord({ data }: { data: Dict }) {
  const { dt, lbl, can, n } = useSession()
  const navigate = useNavigate()
  const d = data.d as Dict
  const section = data.section as SectionMeta
  const overrides = (data.overrides || []) as Dict[]
  const partners = (data.partners || []) as Dict[]
  const history = (data.history || []) as Dict[]

  const [value, setValue] = useState(String(d.effective_value ?? ''))
  const [reason, setReason] = useState('')
  const [partnerCode, setPartnerCode] = useState(
    (partners[0]?.partner_code as string) || '')
  const [partnerValue, setPartnerValue] = useState('')
  const [partnerReason, setPartnerReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const allowed = (d.allowed_values || null) as string[] | null
  const scope = d.scope as string
  const requiresReason = Boolean(d.requires_reason)

  const post = async (path: string, fields: Dict) => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.act(path, fields as never)
      // The handler redirects back to this setting, carrying ?error= when the
      // database refused. Following it rather than interpreting it keeps the
      // message the schema wrote.
      const url = new URL(result.redirect, window.location.origin)
      const refused = url.searchParams.get('error')
      if (refused) { setError(refused); return }
      navigate(0)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHead section={section} title={d.name_en as string}
                subtitle={d.setting_key as string}
                auditLink={`/app/audit/${data.audit_link as string}`} />

      {error && <Note kind="bad"><b>Not changed.</b> {error}</Note>}

      <Cols>
        <Panel title="What this governs" explain="config.setting">
          <p>{d.purpose as string}</p>
          {Boolean(d.name_ar) && <p className="ar" dir="rtl">{d.name_ar as string}</p>}

          <KV rows={[
            ['Key', <code key="k">{d.setting_key as string}</code>],
            ['Area', lbl(d.category)],
            ['Type', `${lbl(d.kind)}${d.unit ? `, in ${d.unit}` : ''}`],
            ['Scope', scope === 'platform'
              ? <div key="s">
                  <Badge tone="warn">platform only</Badge>
                  <div className="xs muted">
                    A partner value is refused by the database, not by this
                    screen, so no import or correction can create one.
                  </div>
                </div>
              : scope === 'partner'
                ? <Badge tone="info" key="s">partner only</Badge>
                : <Badge tone="good" key="s">platform default, partner may override</Badge>],
            ...(d.min_value !== null || d.max_value !== null
              ? [['Permitted range',
                  `${d.min_value ?? '—'} to ${d.max_value ?? '—'}`] as [string, string]]
              : []),
            ...(allowed
              ? [['Permitted values',
                  <span key="a">{allowed.map((v) => (
                    <code key={v} style={{ marginInlineEnd: 6 }}>{v}</code>
                  ))}</span>] as [string, JSX.Element]]
              : []),
            ['Declared default', String(d.default_value)],
          ]} />

          {Boolean(d.is_published || d.pin_on_decision) && (
            <Note kind={d.is_published ? 'warn' : 'info'}>
              {Boolean(d.is_published) && (
                <p><b>Published.</b> Participants have been told this value.
                Changing it changes what the platform promised, so a stated
                reason is required and the change appears on the published
                constants report.</p>
              )}
              {Boolean(d.pin_on_decision) && (
                <p><b>Pinned to decisions.</b> The value in force is stored
                alongside every decision it affected. A decision replayed years
                later reads its own stored copy rather than this page, so
                changing the value here never rewrites a past outcome.</p>
              )}
            </Note>
          )}
        </Panel>

        <Panel title="In force" explain="config.platform">
          <div className="big-value">
            <b className="num">{String(d.effective_value)}</b>
            {Boolean(d.unit) && <span className="muted">{d.unit as string}</span>}
          </div>

          <KV rows={[
            ['Source', <div key="src">
              <Badge tone={d.source === 'platform' ? 'info' : 'muted'}>
                {String(d.source)}
              </Badge>
              <div className="xs muted">
                A value set to the same figure as the default and a value nobody
                set are different facts, which is why this says which applied.
              </div>
            </div>],
            ...(d.platform_set_at
              ? [['Set', `${dt(d.platform_set_at)}${d.set_by_name
                  ? ` by ${d.set_by_name}` : ''}`] as [string, string]]
              : []),
            ...(d.platform_reason
              ? [['Reason given', String(d.platform_reason)] as [string, string]]
              : []),
            ['Partner overrides', n(d.partner_overrides)],
          ]} />

          {can('config.platform', 'update') && scope !== 'partner' && (
            <div className="inline-form">
              <div className="field">
                <label htmlFor="value">Platform value</label>
                {allowed ? (
                  <select id="value" value={value}
                          onChange={(e) => setValue(e.target.value)}>
                    {allowed.map((v) => <option value={v} key={v}>{v}</option>)}
                  </select>
                ) : d.kind === 'boolean' ? (
                  <select id="value" value={value}
                          onChange={(e) => setValue(e.target.value)}>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : (
                  <input id="value" value={value}
                         onChange={(e) => setValue(e.target.value)} />
                )}
              </div>
              <div className="field">
                <label htmlFor="reason">
                  Reason{requiresReason ? ' (required)' : ''}
                </label>
                <input id="reason" value={reason} minLength={requiresReason ? 10 : 0}
                       placeholder="Why this value changes, in a sentence"
                       onChange={(e) => setReason(e.target.value)} />
              </div>
              <button className="btn primary" type="button" disabled={busy}
                      onClick={() => void post('/app/config/set', {
                        setting_key: d.setting_key, value, reason })}>
                Set platform value
              </button>
            </div>
          )}
        </Panel>
      </Cols>

      {scope !== 'platform' && (
        <Panel title="Partner overrides" explain="config.partner">
          {overrides.length > 0 ? (
            <>
              <div className="tbl-wrap">
                <table className="dl">
                  <thead>
                    <tr><th>Partner</th><th>Value</th><th>Reason given</th>
                      <th>Set</th><th className="acts" /></tr>
                  </thead>
                  <tbody>
                    {overrides.map((o) => (
                      <tr key={String(o.partner_code)}>
                        <td data-label="Partner">{o.legal_name_en as string}</td>
                        <td data-label="Value" className="num">
                          <b>{String(o.value)}</b>
                        </td>
                        <td data-label="Reason">{(o.reason as string) || '—'}</td>
                        <td data-label="Set">{dt(o.set_at)}</td>
                        <td className="acts">
                          {can('config.partner', 'delete') && (
                            <button className="btn sm" type="button" disabled={busy}
                                    onClick={() => void post('/app/config/clear', {
                                      setting_key: d.setting_key,
                                      partner_code: o.partner_code })}>
                              Clear
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="xs muted">
                Clearing an override returns the partner to the platform value
                rather than writing the platform value into the partner row,
                which would freeze it against a later platform change.
              </p>
            </>
          ) : (
            <p className="muted">No partner holds a different value.</p>
          )}

          {can('config.partner', 'create') && partners.length > 0 && (
            <div className="inline-form">
              <div className="field">
                <label htmlFor="partner_code">Partner</label>
                <select id="partner_code" value={partnerCode}
                        onChange={(e) => setPartnerCode(e.target.value)}>
                  {partners.map((p) => (
                    <option value={p.partner_code as string} key={p.partner_code as string}>
                      {p.legal_name_en as string}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="pvalue">Value</label>
                <input id="pvalue" value={partnerValue}
                       onChange={(e) => setPartnerValue(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="preason">Reason</label>
                <input id="preason" value={partnerReason} minLength={10}
                       placeholder="Why this partner differs"
                       onChange={(e) => setPartnerReason(e.target.value)} />
              </div>
              <button className="btn" type="button" disabled={busy || !partnerValue}
                      onClick={() => void post('/app/config/set-partner', {
                        setting_key: d.setting_key, partner_code: partnerCode,
                        value: partnerValue, reason: partnerReason })}>
                Set partner value
              </button>
            </div>
          )}
        </Panel>
      )}

      <Panel title="Change history" explain="audit.record_trail" tight>
        {history.length > 0 ? (
          <>
            <div className="tbl-wrap">
              <table className="dl">
                <thead>
                  <tr><th>When</th><th>Who</th><th>Acting as</th>
                    <th>Was</th><th>Became</th><th>Reason</th></tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i}>
                      <td data-label="When">{dt(h.changed_at)}</td>
                      <td data-label="Who">{(h.actor_label as string) || '—'}</td>
                      <td data-label="Acting as">
                        <code>{(h.acting_role as string) || '—'}</code>
                      </td>
                      <td data-label="Was" className="num">{(h.was as string) || '—'}</td>
                      <td data-label="Became" className="num">
                        {(h.now_value as string) || '—'}
                      </td>
                      <td data-label="Reason">{(h.reason as string) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="xs muted">
              This is the audit trail filtered, not a second log.
            </p>
          </>
        ) : (
          <p className="muted">
            No recorded changes. This setting has only ever held its declared
            default.
          </p>
        )}
      </Panel>
    </>
  )
}
